import { withGitpodSize } from "@/assets/icons/utils";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { ErrorFallback } from "@/components/ErrorFallback";
import { Button } from "@/components/flexkit/Button";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { useListPrompts, type PlainPrompt } from "@/queries/prompt-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import IconPlusSmall from "central-icons/IconPlusSmall";
import { FileDown } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { AgentSkillCard } from "@/routes/settings/organization/agents/AgentSkillCard";
import { NewSkillModal } from "@/routes/settings/organization/agents/NewSkillModal";
import { ImportDefaultSkillsDialog } from "@/routes/settings/organization/agents/ImportDefaultSkillsDialog";

export const AgentSkills: FC = () => {
    const { data: user, isPending: isUserPending, error: userError } = useAuthenticatedUser();
    const { data: skills, isPending, error } = useListPrompts({ isCommand: true });

    const [showNewSkillModal, setShowNewSkillModal] = useState(false);
    const [showImportDefaultsModal, setShowImportDefaultsModal] = useState(false);

    const onNewSkill = useCallback(() => {
        setShowNewSkillModal(true);
    }, []);

    const onImportDefaults = useCallback(() => {
        setShowImportDefaultsModal(true);
    }, []);

    const content = useMemo(() => {
        if (error || userError) {
            return <ErrorFallback error={error || userError!} />;
        }

        if (isPending || isUserPending || !user) {
            return (
                <SkeletonBlock
                    ready={!isPending && !isUserPending}
                    failed={!!error || !!userError}
                    className="h-[160px] w-full"
                />
            );
        }

        return (
            <AgentSkillsContent
                skills={skills?.prompts ?? []}
                onNewSkill={onNewSkill}
                onImportDefaults={onImportDefaults}
            />
        );
    }, [error, userError, isPending, isUserPending, user, skills, onNewSkill, onImportDefaults]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <Heading3>Skills</Heading3>
            </div>
            <div data-testid="agent-skills-section">
                {content}
                <NewSkillModal open={showNewSkillModal} onClose={() => setShowNewSkillModal(false)} />
                <ImportDefaultSkillsDialog
                    open={showImportDefaultsModal}
                    onClose={() => setShowImportDefaultsModal(false)}
                />
            </div>
        </div>
    );
};

const AgentSkillsContent: FC<{
    skills: PlainPrompt[];
    onNewSkill: () => void;
    onImportDefaults: () => void;
}> = ({ skills, onNewSkill, onImportDefaults }) => {
    if (skills.length === 0) {
        return (
            <EmptyStateCard
                title="No agent skills"
                description="Create custom skills that can be used across your organization to streamline common AI tasks."
                data-testid="agent-skills-empty-state"
            >
                <div className="flex gap-2">
                    <Button
                        onClick={onImportDefaults}
                        data-testid="import-defaults-empty-state-button"
                        data-tracking-id="import-default-skills-empty-state"
                    >
                        Import Defaults
                    </Button>
                    <Button
                        onClick={onNewSkill}
                        variant="secondary"
                        data-testid="add-skill-modal-trigger"
                        data-tracking-id="add-agent-skill-agent-skills-content"
                    >
                        New Skill
                    </Button>
                </div>
            </EmptyStateCard>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-4">
                <Text className="text-base text-content-secondary">
                    These are organization-wide skills that can be used by any member to interact with AI agents.
                </Text>
            </div>
            <SkillGrid skills={skills} onNewSkill={onNewSkill} onImportDefaults={onImportDefaults} />
        </div>
    );
};

const SkillGrid: FC<{
    skills: PlainPrompt[];
    onNewSkill: () => void;
    onImportDefaults: () => void;
}> = ({ skills, onNewSkill, onImportDefaults }) => {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredSkills = useMemo(() => {
        if (!searchQuery.trim()) return skills;
        const query = searchQuery.toLowerCase().replace(/^\//, "");
        return skills.filter(
            (skill) =>
                skill.metadata?.name?.toLowerCase().includes(query) ||
                skill.metadata?.description?.toLowerCase().includes(query) ||
                skill.spec?.command?.toLowerCase().includes(query),
        );
    }, [skills, searchQuery]);

    const showSearch = skills.length > 1;

    return (
        <>
            <div className="flex items-center justify-between gap-4">
                {showSearch ? (
                    <SearchInput
                        wrapperClassName="flex-1"
                        className="max-w-none"
                        placeholder="Search skills by name, description, or trigger"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery("")}
                        data-testid="skill-search-input"
                    />
                ) : (
                    <div className="flex-1" />
                )}
                <ButtonGroup variant="primary">
                    <ButtonGroupItem
                        LeadingIcon={withGitpodSize(IconPlusSmall)}
                        onClick={onNewSkill}
                        data-testid="add-skill-button-inline"
                        data-tracking-id="add-agent-skill-agent-skills-content-inline"
                    >
                        New Skill
                    </ButtonGroupItem>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <ButtonGroupItem
                                LeadingIcon={IconChevronDownSmall}
                                aria-label="More skill options"
                                data-testid="add-skill-dropdown-trigger"
                            />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={onImportDefaults}
                                data-testid="import-defaults-menu-item"
                                data-tracking-id="import-default-skills-menu"
                            >
                                <FileDown className="mr-2 size-4" />
                                Import Defaults
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </ButtonGroup>
            </div>
            {filteredSkills.length === 0 ? (
                <EmptyStateCard
                    title="No matching skills"
                    description="No skills match your search. Try a different search term."
                    data-testid="no-matching-skills-empty-state"
                />
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                    {filteredSkills.map((skill) => (
                        <AgentSkillCard key={skill.id} skill={skill} />
                    ))}
                </div>
            )}
        </>
    );
};
